/**
 * Files pasted or dropped into a composer (slice 5b spec §6.2): the same
 * files the attach button takes. A screenshot from the clipboard comes as
 * `image.png` or with no name at all, so it's named for when it was pasted
 * ("Pasted image 2026-10-23 14.05.png"), as macOS names a screenshot.
 */

/** What a paste or a drop carries: the parts of a `DataTransfer` read here. */
export type Carried = {
  items?: ArrayLike<{ kind: string; getAsFile: () => File | null }>;
  files?: ArrayLike<File>;
  getData: (type: string) => string;
};

/** The name a browser gives an image with none of its own: Chrome's, Safari's and Firefox's `image.png`. */
const NAMELESS = /^(image|untitled)?(\.[a-z0-9]+)?$/i;

/** An image type's extension, as Crew keeps it (§5.3); any other photo keeps its subtype and is refused as the attach button refuses it. */
const IMAGE_EXT: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/gif": "gif",
  "image/webp": "webp",
  "image/heic": "heic",
  "image/heif": "heif",
};

const two = (n: number) => String(n).padStart(2, "0");

/** "Pasted image 2026-10-23 14.05.png", in the person's own time; "(2)" on the second of a paste. */
export function pastedName(mime: string, at: Date, nth = 1): string {
  const ext = IMAGE_EXT[mime] ?? (mime.split("/")[1] || "png");
  const day = `${at.getFullYear()}-${two(at.getMonth() + 1)}-${two(at.getDate())}`;
  const time = `${two(at.getHours())}.${two(at.getMinutes())}`;
  return `Pasted image ${day} ${time}${nth > 1 ? ` (${nth})` : ""}.${ext}`;
}

/** The files carried, in order: the items' when there are any (a paste), else `files` (a drop). */
function carriedFiles(data: Carried): File[] {
  const fromItems = Array.from(data.items ?? [])
    .filter((item) => item.kind === "file")
    .flatMap((item) => item.getAsFile() ?? []);
  return fromItems.length ? fromItems : Array.from(data.files ?? []);
}

/** Images with a stand-in name get a pasted one; every other file keeps its own. */
function named(files: File[], at: Date, nameless: (name: string) => boolean): File[] {
  let nth = 0;
  return files.map((file) => {
    if (!file.type.startsWith("image/") || !nameless(file.name)) return file;
    nth += 1;
    return new File([file], pastedName(file.type, at, nth), {
      type: file.type,
      lastModified: file.lastModified,
    });
  });
}

/** The addresses of the images in a paste's HTML: what Copy Image puts beside the image. */
function imageSources(html: string): string[] {
  return [...html.matchAll(/<img\b[^>]*?\bsrc\s*=\s*["']?([^"'\s>]+)/gi)].map((m) =>
    m[1]!.replaceAll("&amp;", "&"),
  );
}

/** An address whose path names an image file, as a bare Copy Image's does with no HTML. */
const IMAGE_URL = /^https?:\/\/[^?#\s]+\.(png|jpe?g|gif|webp|heic|heif)([?#]\S*)?$/i;

/**
 * Whether the text is only what came with the files: Finder's file names, or
 * the image's own address from Copy Image (its `<img src>` in the HTML, or,
 * with no HTML, an image file's address). Any other words or link is to paste
 * (Devin on #492: a link beside an inline image is what was copied).
 */
function onlyNamesThem(text: string, html: string, files: File[]): boolean {
  const sources = imageSources(html);
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  return lines.every(
    (line) =>
      files.some((f) => f.name === line) ||
      sources.includes(line) ||
      (!html && IMAGE_URL.test(line)),
  );
}

/**
 * The files a paste attaches, or none to let it paste as usual: none when it
 * has no files, or when it has words too (a rich-text paste's inline image
 * stays with its words, which are what was copied).
 */
export function pastedFiles(data: Carried, at = new Date()): File[] {
  const files = carriedFiles(data);
  if (!files.length) return [];
  if (!onlyNamesThem(data.getData("text/plain"), data.getData("text/html"), files)) return [];
  return named(files, at, (name) => NAMELESS.test(name));
}

/**
 * The files a drop attaches: all of them. A dropped file's name is its own,
 * even `image.png`; only one with none is named as a paste's.
 */
export function droppedFiles(data: Carried, at = new Date()): File[] {
  return named(carriedFiles(data), at, (name) => !name);
}
